import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { MainMenuSub } from './main-menu-sub';
import defaultLogoUrl from '@/assets/logos/resurs_logo_rgb_black.svg';
import { useGlobalQueries } from '@/hooks/use-global-queries';
import { useNavItem } from '@/hooks/use-nav-item';
import { useLocalization } from '@/hooks/use-localization';
import { useNavMenus } from '@/hooks/use-nav-menus';
import { removeEnclosingSlashes } from '@/utils/strings';
import { Login } from './login';
import { MarketType } from '@/queries/page';

export const MainMenu = ({ onSearchButtonClick }: { onSearchButtonClick: () => void }) => {
    const { pathname } = useLocation();
    const { localize } = useLocalization();
    const { isCurrentItemActive } = useNavItem();
    const { getMarketType } = useGlobalQueries();
    const { getMainMenuItems, getSubMenuItems, headerSettings } = useNavMenus();

    const marketType = getMarketType();
    const marketLogos = headerSettings.data?.market_logos;
    const marketLogoLinks = headerSettings.data?.market_logo_links;
    const marketLogo = marketType && marketType !== 'cluster' ? marketLogos?.[marketType as Exclude<MarketType, 'cluster'>] : false;
    const marketLogoLink = marketType && marketType !== 'cluster' ? marketLogoLinks?.[marketType as Exclude<MarketType, 'cluster'>] : false;
    const logoUrl = marketLogo && marketLogo.url ? marketLogo.url : defaultLogoUrl;
    const logoWidth = marketLogo && marketLogo.width ? marketLogo.width : 190;
    const logoHeight = marketLogo && marketLogo.height ? marketLogo.height : 29;
    const isCustomLogo = !!(marketLogo && marketLogo.url);

    const getHomeLink = () => {
        if (marketLogoLink && marketLogoLink.url) {
            return marketLogoLink.url;
        }

        if (getMarketType() !== 'cluster') {
            return '/';
        }

        return removeEnclosingSlashes(pathname).split('/')[0] ?? '';
    };

    return (
        <div data-component="main-menu">
            <div className={clsx('r-container r-px-8', { 'r-py-0.5': !isCustomLogo })}>
                <div className="r-flex r-items-center r-justify-between">
                    <Link className="r-block r-p-1.5" to={getHomeLink()} aria-label={localize('toStart', true)}>
                        <img
                            className={clsx(isCustomLogo ? 'max-xl:r-max-w-[190px] xl:r-h-12 xl:r-w-auto' : 'r-max-w-[190px]')}
                            src={logoUrl}
                            alt={marketLogo && marketLogo.alt ? marketLogo.alt : 'Resurs Bank logo'}
                            width={logoWidth}
                            height={logoHeight}
                        />
                    </Link>
                    <div className="r-flex r-gap-6 r-py-6">
                        <nav aria-label={localize('main-menu')} className="r-flex r-gap-6">
                            <ul className="r-flex r-items-center r-gap-5 r-font-bold">
                                {getMainMenuItems().map((item, index) => (
                                    <li
                                        key={index}
                                        className={clsx(
                                            'r-rounded-xl r-px-2 r-py-1 r-transition-all r-duration-200 hover:r-bg-tertiary-500 hover:r-text-white',
                                            {
                                                'r-bg-tertiary-500 r-text-white': isCurrentItemActive(item),
                                            }
                                        )}
                                    >
                                        <Link className="r-py-1.5" to={item.url.replace(/\/$/, '')} target={item.target || '_self'}>
                                            {item.title}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                        <ul className="r-flex r-gap-6">
                            <li className="r-flex r-items-center">
                                <button className="r-flex r-items-center r-gap-[2px] r-font-bold" onClick={() => onSearchButtonClick()}>
                                    <span className="r-flex r-rounded-xl r-px-2 r-py-1 r-transition-all hover:r-bg-tertiary-500 hover:r-text-white">
                                        <svg
                                            role="img"
                                            aria-hidden="true"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <g id="Search" clipPath="url(#clip0_3350_11459)">
                                                <path
                                                    id="Vector"
                                                    d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z"
                                                    fill="currentColor"
                                                />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_3350_11459">
                                                    <rect width="24" height="24" fill="white" />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                        <span>{localize('search')}</span>
                                    </span>
                                </button>
                            </li>
                            <li>
                                <Login />
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <MainMenuSub menu={getSubMenuItems()} />
            <MainMenuSub menu={getSubMenuItems(2)} depth={2} />
        </div>
    );
};
